import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { getConfig } from '../../../core/config/environment';
import { updateRecordApi, uploadDocumentApi, deleteDocumentApi, lookupDvhcApi, updateCsdtStatusApi, convertOneDvhcApi, convertDvhcTextApi, updatePortraitApi, fetchRecordDetail } from '../services/trungTamApi';
import PortraitUploader from './PortraitUploader';
import DvhcAutocomplete from './DvhcAutocomplete';
import TeacherAutocomplete from './TeacherAutocomplete';
import type { TtStudentRecord, TtStudentDocument, DocType, CourseStatus, CsdtStatus } from '../types';

const assetUrl = (path: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith('data:') || path.startsWith('http')) return path;
  return `${getConfig().API_BASE_URL}${path}`;
};

/** Prefer base64 data URL (already in API response) over asset URL path */
const imgSrc = (base64: string | null | undefined, urlPath: string | null): string | null =>
  base64 || assetUrl(urlPath);

/** Format raw date (DDMMYYYY, DD-MM-YYYY, etc.) → DD/MM/YYYY */
const fmtDate = (v: string | null): string => {
  if (!v) return '';
  const s = v.trim();
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 2)}/${s.slice(2, 4)}/${s.slice(4)}`;
  if (/^\d{2}[.-]\d{2}[.-]\d{4}$/.test(s)) return s.replace(/[.-]/g, '/');
  return s;
};

type Props = {
  records: TtStudentRecord[];
  loading: boolean;
  canWrite: boolean;
  courseStatus?: CourseStatus;
  courseId?: number;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onToggleReviewed: (id: number, reviewed: boolean) => Promise<void>;
  onOpenTransferModal: () => void;
  /** Open transfer modal seeded with a single record id. Parent sets
   *  selectedIds=[id] then opens the shared bulk-transfer modal. */
  onOpenSingleTransfer?: (id: number) => void;
  onDelete: (id: number) => Promise<void>;
  onRecordUpdated?: (updated: TtStudentRecord) => void;
  onRequestRecordsReload?: (page?: number) => void;
  /** Pagination — passed from parent when API uses server-side paging. */
  page?: number;
  total?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
};

const MAGNIFY_KEYS = new Set(['hoTen', 'diaChi', 'maDvhc']);

const DETAIL_FIELDS = [
  { key: 'soCCCD', label: 'CCCD', icon: 'badge' },
  { key: 'soCMT', label: 'CMT', icon: 'credit_card' },
  { key: 'hoTen', label: 'Họ tên', icon: 'person' },
  { key: 'ngaySinh', label: 'Ngày sinh', icon: 'cake' },
  { key: 'gioiTinh', label: 'Giới tính', icon: 'wc' },
  { key: 'ngayCap', label: 'Ngày cấp CCCD', icon: 'event' },
  { key: 'diaChi', label: 'Địa chỉ (gốc)', icon: 'home' },
  { key: 'maDvhc', label: 'Đơn vị hành chính', icon: 'location_city' },
] as const;

// Fields that should span the full row in the edit grid — long text that
// must not be truncated (CCCD's raw address line, resolved DVHC name).
const WIDE_FIELDS = new Set(['diaChi', 'maDvhc']);

// noiCapGPLX + donViCapGPLX removed per operator request — neither field
// was actionable for the course registration workflow, and both tend to
// duplicate data already in hangGPLX / ngayCapGPLX. Data columns remain
// in the DB for backward compatibility with old records; we just stop
// displaying / editing them.
/**
 * Derive years-of-driving from ngayTrungTuyen (exam pass date).
 * Mirrors the helper in TtScannerForm — kept local to avoid pulling
 * the scanner's big component into the records table bundle.
 *   null     — unparseable / future / empty
 *   { years, warning }  — years=fractional, warning = years < 3
 */
const calcDrivingYearsSafe = (raw: string | null | undefined): { years: number; warning: boolean } | null => {
  if (!raw) return null;
  const m = String(raw).trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!m) return null;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  let y = parseInt(m[3], 10);
  if (y < 100) y += y < 50 ? 2000 : 1900;
  if (!d || !mo || y < 1970 || y > 2100) return null;
  const passDate = new Date(y, mo - 1, d);
  if (Number.isNaN(passDate.getTime())) return null;
  const now = new Date();
  if (passDate > now) return null;
  const years = (now.getTime() - passDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  return { years, warning: years < 3 };
};

const GPLX_DETAIL_FIELDS: readonly { key: string; label: string; icon: string; dropdown?: boolean }[] = [
  { key: 'soGPLX', label: 'Số GPLX', icon: 'directions_car' },
  { key: 'hangGPLX', label: 'Hạng GPLX (cũ)', icon: 'class' },
  { key: 'hangGPLXMoi', label: 'Hạng GPLX (mới)', icon: 'swap_horiz', dropdown: true },
  { key: 'ngayCapGPLX', label: 'Ngày cấp GPLX', icon: 'event' },
  { key: 'ngayHetHanGPLX', label: 'Ngày hết hạn', icon: 'event_busy' },
  { key: 'ngayTrungTuyenGPLX', label: 'Ngày trúng tuyển', icon: 'emoji_events' },
];

const HANG_GPLX_MOI_OPTIONS = [
  'A.01', 'A.02', 'A.03', 'A.04', 'A11', 'A1m', 'Am',
  'B', 'B.01', 'B.02', 'B.03', 'B.04', 'B.05', 'B1m', 'BE',
  'C1', 'C1E', 'Cm', 'CE',
  'D1', 'D1E', 'D2', 'D2E', 'Dm', 'DE',
];

const METHOD_MAP: Record<string, { label: string; icon: string; cls: string }> = {
  qr: { label: 'QR', icon: 'qr_code', cls: 'tt-records__method--qr' },
  ocr: { label: 'OCR', icon: 'document_scanner', cls: 'tt-records__method--ocr' },
  manual: { label: 'Thủ công', icon: 'edit', cls: 'tt-records__method--manual' },
};

const DOC_TYPES: { value: DocType; label: string; icon: string }[] = [
  { value: 'cccd', label: 'CCCD', icon: 'badge' },
  { value: 'gplx', label: 'GPLX', icon: 'directions_car' },
  { value: 'health', label: 'Khám sức khỏe', icon: 'medical_services' },
  { value: 'other', label: 'Khác', icon: 'description' },
];

const CSDT_OPTIONS: { value: CsdtStatus; label: string; icon: string; cls: string }[] = [
  { value: 'none', label: 'Chưa nhập', icon: 'remove', cls: 'tt-records__csdt-badge--none' },
  { value: 'entered', label: 'Đã nhập', icon: 'edit_note', cls: 'tt-records__csdt-badge--entered' },
  { value: 'verified', label: 'Đã đối sánh', icon: 'fingerprint', cls: 'tt-records__csdt-badge--verified' },
  { value: 'completed', label: 'Hoàn thành', icon: 'check_circle', cls: 'tt-records__csdt-badge--completed' },
  { value: 'failed', label: 'Không hoàn thành', icon: 'cancel', cls: 'tt-records__csdt-badge--failed' },
];

type EditDraft = Record<string, string>;
type SortCol = 'index' | 'hoTen' | 'soCCCD' | 'dvhc' | 'teacher' | 'scanMethod' | 'hasGplx' | 'reviewed' | 'csdtStatus' | 'createdAt';
type SortDir = 'asc' | 'desc';

const getGivenName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  return parts[parts.length - 1] || fullName;
};

const TtRecordsTable: React.FC<Props> = ({
  records, loading, canWrite, courseStatus, courseId, selectedIds, onToggleSelect,
  onToggleReviewed, onOpenTransferModal, onOpenSingleTransfer, onDelete, onRecordUpdated, onRequestRecordsReload,
  page = 1, total, pageSize = 50, onPageChange,
}) => {
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Cache of fully-loaded records (with base64 images). Fetched on demand
  // when the operator expands a row. Keyed by record id.
  const [detailCache, setDetailCache] = useState<Record<number, TtStudentRecord>>({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [viewImageUrl, setViewImageUrl] = useState<string | null>(null);

  // Ref so the keydown handler reads the current filtered list without
  // reattaching on every filter keystroke. Sync'd after `filtered` is
  // computed below.
  const filteredRef = useRef<TtStudentRecord[]>([]);

  // Fetch full detail (with base64 images) on demand, cache by id.
  // The list API no longer includes any base64 blobs so this is required
  // before the detail modal can show images.
  const openDetail = useCallback(async (id: number) => {
    setExpandedId(id);
    setConfirmDeleteId(null);
    if (detailCache[id]) return; // already cached — use it immediately
    setDetailLoading(true);
    try {
      const res = await fetchRecordDetail(id);
      if (res.EC === 0 && res.DT) {
        setDetailCache(prev => ({ ...prev, [id]: res.DT }));
      }
    } catch {
      // Non-fatal — detail panel falls back to list-level data (no images)
    } finally {
      setDetailLoading(false);
    }
  }, [detailCache]);

  // Arrow-key navigation + Esc-to-close while the detail modal is
  // open. Ignored when typing in an input/textarea so the operator's
  // keyboard entry isn't hijacked. Uses capture so this wins over the
  // course-detail page's back-navigation Esc handler.
  useEffect(() => {
    if (!expandedId) return;
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (tgt && /^(INPUT|TEXTAREA|SELECT)$/.test(tgt.tagName)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setExpandedId(null);
        setEditingId(null);
        setConfirmDeleteId(null);
        return;
      }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      setExpandedId(prev => {
        if (prev == null) return prev;
        const list = filteredRef.current;
        const idx = list.findIndex(r => r.id === prev);
        if (idx < 0) return prev;
        const delta = e.key === 'ArrowLeft' ? -1 : 1;
        const next = list[idx + delta];
        if (!next) return prev;
        e.preventDefault();
        return next.id;
      });
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [expandedId]);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft>({});
  const [saving, setSaving] = useState(false);
  const [editTeacherId, setEditTeacherId] = useState<number | null | undefined>(undefined);
  const [editTeacherName, setEditTeacherName] = useState<string | null>(null);
  const avatarFileRef = useRef<HTMLInputElement | null>(null);
  const [editAvatarPreview, setEditAvatarPreview] = useState<string | null>(null);
  const [zoomAvatarUrl, setZoomAvatarUrl] = useState<string | null>(null);

  // Source image edit state
  const sourceFileRef = useRef<HTMLInputElement | null>(null);
  const [editSourcePreview, setEditSourcePreview] = useState<string | null>(null);

  // Portrait ("ảnh thẻ") edit state — held separately from editDraft because
  // portraitBase64 can be huge and we don't want it spreading into other
  // payload shapes. Persisted via a dedicated endpoint on save.
  const [editPortraitBase64, setEditPortraitBase64] = useState<string | null>(null);
  // Raw selfie before AI enhance — kept so the operator can compare
  // with the AI version in the detail popup.
  const [editPortraitOriginalBase64, setEditPortraitOriginalBase64] = useState<string | null>(null);
  // JSON array of all AI variants — user gen nhiều lần, chọn 1 làm avatar.
  const [editPortraitAiVariantsJson, setEditPortraitAiVariantsJson] = useState<string | null>(null);
  const [editFaceMatchScore, setEditFaceMatchScore] = useState<number | null>(null);

  // ĐVHC display text for autocomplete
  const [dvhcDisplayText, setDvhcDisplayText] = useState('');

  // Document upload state
  const docFileRef = useRef<HTMLInputElement | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docUploadRecordId, setDocUploadRecordId] = useState<number | null>(null);
  const [docType, setDocType] = useState<DocType>('gplx');
  const [localDocs, setLocalDocs] = useState<Record<number, TtStudentDocument[]>>({});

  // Per-row convert state — tracks which record id is currently being converted
  const [convertingRowId, setConvertingRowId] = useState<number | null>(null);
  // Live auto-convert in the edit modal — spinner on the DVHC field
  const [autoConvertBusy, setAutoConvertBusy] = useState(false);

  const failedCount = useMemo(
    () => records.filter(r => r.dvhcStatus === 'failed').length,
    [records],
  );

  // Per-row convert: triggered by the small button in the "Đơn vị hành chính"
  // cell. Uses the diachi.io cache first, then upstream, then falls back to a
  // direct tinhthanhpho lookup for already-2-tier addresses.
  const handleConvertOneRecord = useCallback(async (recordId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (convertingRowId !== null) return;
    setConvertingRowId(recordId);
    try {
      const resp = await convertOneDvhcApi(recordId);
      if (resp.EC === 0) {
        if (resp.DT?.notSure) {
          toast.warning(resp.EM || 'Đã chuyển đổi (không chắc chắn)');
        } else {
          toast.success(resp.EM || 'Đã chuyển đổi');
        }
        onRequestRecordsReload?.();
      } else {
        toast.error(resp.EM || 'Không chuyển đổi được');
      }
    } catch (err: unknown) {
      const e2 = err as { response?: { status?: number; data?: { EM?: string } } };
      toast.error(e2?.response?.data?.EM || 'Lỗi kết nối server');
    } finally {
      setConvertingRowId(null);
    }
  }, [convertingRowId, onRequestRecordsReload]);

  // Live auto-convert in the edit modal: when the user changes the raw
  // address (`diaChi`) field, debounce 800ms then resolve the DVHC. The
  // resolved (maDvhc, tenDayDu) is written into the edit draft so the
  // DvhcAutocomplete below shows the match without a save round-trip.
  // Skips the very first run after startEdit so it doesn't re-resolve the
  // already-persisted address.
  const lastAutoConvertedDiaChi = useRef<string | null>(null);
  useEffect(() => {
    if (!editingId) {
      lastAutoConvertedDiaChi.current = null;
      return;
    }
    const diaChi = (editDraft.diaChi || '').trim();
    if (!diaChi || diaChi.length < 8) return;
    if (lastAutoConvertedDiaChi.current === null) {
      // First effect run after startEdit — record the initial value but don't
      // call the API. Only subsequent user edits should trigger conversion.
      lastAutoConvertedDiaChi.current = diaChi;
      return;
    }
    if (lastAutoConvertedDiaChi.current === diaChi) return;

    const handle = setTimeout(async () => {
      setAutoConvertBusy(true);
      try {
        const resp = await convertDvhcTextApi(diaChi);
        if (resp.EC === 0 && resp.DT) {
          lastAutoConvertedDiaChi.current = diaChi;
          setEditDraft((prev) => ({ ...prev, maDvhc: resp.DT!.maDvhc || '' }));
          setDvhcDisplayText(resp.DT.tenDayDu || '');
        }
      } catch {
        // Silent on auto-convert failures — the user can still pick from the
        // DvhcAutocomplete dropdown manually.
      } finally {
        setAutoConvertBusy(false);
      }
    }, 800);
    return () => clearTimeout(handle);
  }, [editingId, editDraft.diaChi]);

  // Magnify tooltip
  const [tipText, setTipText] = useState('');
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const [tipVisible, setTipVisible] = useState(false);
  const tipTimer = useRef<ReturnType<typeof setTimeout>>();

  const showTip = useCallback((e: React.MouseEvent, text: string) => {
    clearTimeout(tipTimer.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTipText(text);
    setTipPos({ x: rect.left, y: rect.top - 6 });
    tipTimer.current = setTimeout(() => setTipVisible(true), 350);
  }, []);

  const hideTip = useCallback(() => {
    clearTimeout(tipTimer.current);
    setTipVisible(false);
  }, []);

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const filtered = useMemo(() => {
    let list = records;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        r.hoTen.toLowerCase().includes(q) ||
        r.soCCCD.includes(q) ||
        (r.soCMT || '').includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      switch (sortCol) {
        case 'hoTen': return getGivenName(a.hoTen).localeCompare(getGivenName(b.hoTen), 'vi') * dir;
        case 'soCCCD': return (a.soCCCD || '').localeCompare(b.soCCCD || '') * dir;
        case 'dvhc': return (a.dvhcTenDayDu || '').localeCompare(b.dvhcTenDayDu || '', 'vi') * dir;
        case 'teacher': return (a.teacher?.username || '').localeCompare(b.teacher?.username || '', 'vi') * dir;
        case 'scanMethod': return (a.scanMethod || '').localeCompare(b.scanMethod || '') * dir;
        case 'hasGplx': return ((a.soGPLX ? 1 : 0) - (b.soGPLX ? 1 : 0)) * dir;
        case 'reviewed': return ((a.reviewed ? 1 : 0) - (b.reviewed ? 1 : 0)) * dir;
        case 'csdtStatus': return (a.csdtStatus || '').localeCompare(b.csdtStatus || '') * dir;
        case 'createdAt': return (new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()) * dir;
        default: return 0;
      }
    });
  }, [records, search, sortCol, sortDir]);

  // Sync the latest filtered list into the ref on every render so the
  // arrow-key handler above always reads the current order.
  filteredRef.current = filtered;

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    await onDelete(id);
    setConfirmDeleteId(null);
    if (expandedId === id) setExpandedId(null);
  };

  const handleEditSourceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setEditSourcePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    if (sourceFileRef.current) sourceFileRef.current.value = '';
  };

  // Single-record transfer delegates to the parent so the existing
  // TransferRecordsModal can be reused — we just ask the parent to
  // seed selectedIds with this one record and open the modal.

  const startEdit = (r: TtStudentRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(r.id);
    setEditAvatarPreview(null);
    setEditSourcePreview(null);
    setEditTeacherId(r.teacher?.id ?? null);
    setEditTeacherName(r.teacher?.username ?? null);
    // Seed portrait fields from the record so the UI can show any previous
    // upload + score when the operator re-opens an already-populated row.
    setEditPortraitBase64(r.portraitBase64 ?? null);
    setEditPortraitOriginalBase64(r.portraitOriginalBase64 ?? null);
    setEditPortraitAiVariantsJson(r.portraitAiVariantsJson ?? null);
    setEditFaceMatchScore(r.faceMatchScore ?? null);
    // Load ĐVHC display text — prefer the persisted column, fall back to API.
    const persistedName = r.dvhcTenDayDu || r.dvhcInfo?.tenDayDu || '';
    setDvhcDisplayText(persistedName);
    if (r.maDvhc && !persistedName) {
      lookupDvhcApi(r.maDvhc).then(res => {
        if (res.EC === 0 && res.DT) setDvhcDisplayText(res.DT.tenDayDu);
      }).catch(() => {});
    }
    setEditDraft({
      soCCCD: r.soCCCD || '',
      soCMT: r.soCMT || '',
      hoTen: r.hoTen || '',
      ngaySinh: r.ngaySinh || '',
      gioiTinh: r.gioiTinh || '',
      diaChi: r.diaChi || '',
      maDvhc: r.maDvhc || '',
      ngayCap: r.ngayCap || '',
      soGPLX: r.soGPLX || '',
      hangGPLX: r.hangGPLX || '',
      hangGPLXMoi: r.hangGPLXMoi || '',
      ngayCapGPLX: r.ngayCapGPLX || '',
      noiCapGPLX: r.noiCapGPLX || '',
      donViCapGPLX: r.donViCapGPLX || '',
      ngayTrungTuyenGPLX: r.ngayTrungTuyenGPLX || '',
      ngayHetHanGPLX: r.ngayHetHanGPLX || '',
    });
    if (expandedId !== r.id) void openDetail(r.id);
  };

  const cancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(null);
    setEditDraft({});
    setEditAvatarPreview(null);
    setEditSourcePreview(null);
    setEditPortraitBase64(null);
    setEditPortraitOriginalBase64(null);
    setEditPortraitAiVariantsJson(null);
    setEditFaceMatchScore(null);
    setEditTeacherId(undefined);
  };

  const handleEditField = (key: string, value: string) => {
    setEditDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setEditAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    if (avatarFileRef.current) avatarFileRef.current.value = '';
  };

  const saveEdit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editingId || saving) return;

    if (!editDraft.soCCCD || !editDraft.hoTen) {
      toast.error('CCCD và Họ tên không được để trống');
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, string | number | null | undefined> = { ...editDraft };
      // Include dvhc display text so backend doesn't have to re-resolve from API.
      // Only send when autocomplete actually has a selection (value + text).
      if (editDraft.maDvhc && dvhcDisplayText) {
        payload.dvhcTenDayDu = dvhcDisplayText;
      }
      // Send teacherId only if the operator touched it (undefined = untouched)
      if (editTeacherId !== undefined) {
        payload.teacherId = editTeacherId;
      }
      if (editAvatarPreview) {
        payload.avatarBase64 = editAvatarPreview;
      }
      if (editSourcePreview) {
        payload.sourceImageBase64 = editSourcePreview;
      }

      const resp = await updateRecordApi(editingId, payload);
      if (resp.EC === 0) {
        // Persist the portrait + face-match score via its dedicated endpoint
        // so the huge base64 blob doesn't have to ride along every normal
        // record update. Only fires when the operator actually touched it.
        const portraitChanged =
          editPortraitBase64 !== (resp.DT?.portraitBase64 ?? null) ||
          editPortraitOriginalBase64 !== (resp.DT?.portraitOriginalBase64 ?? null) ||
          editPortraitAiVariantsJson !== (resp.DT?.portraitAiVariantsJson ?? null);
        if (portraitChanged) {
          try {
            await updatePortraitApi(editingId, {
              portraitBase64: editPortraitBase64,
              portraitOriginalBase64: editPortraitOriginalBase64,
              portraitAiVariantsJson: editPortraitAiVariantsJson,
              faceMatchScore: editFaceMatchScore,
            });
          } catch {
            toast.warning('Cập nhật hồ sơ thành công nhưng không lưu được ảnh thẻ');
          }
        }
        toast.success('Cập nhật thành công');
        setEditingId(null);
        setEditDraft({});
        setEditAvatarPreview(null);
        setEditSourcePreview(null);
        setEditPortraitBase64(null);
        setEditPortraitOriginalBase64(null);
        setEditPortraitAiVariantsJson(null);
        setEditFaceMatchScore(null);
        setEditTeacherId(undefined);
        if (resp.DT) {
          const merged = { ...resp.DT } as TtStudentRecord;
          if (editAvatarPreview) merged.avatarBase64 = editAvatarPreview;
          if (editSourcePreview) merged.sourceImageBase64 = editSourcePreview;
          if (portraitChanged) {
            merged.portraitBase64 = editPortraitBase64;
            merged.portraitOriginalBase64 = editPortraitOriginalBase64;
            merged.portraitAiVariantsJson = editPortraitAiVariantsJson;
            merged.faceMatchScore = editFaceMatchScore;
          }
          // Sync teacher assignment into the cached record
          if (editTeacherId !== undefined) {
            merged.teacher = editTeacherId ? { id: editTeacherId, username: editTeacherName || '', email: '' } : null;
          }
          // Keep detail cache in sync so reopening the detail panel
          // immediately shows the latest saved data without a re-fetch.
          setDetailCache(prev => ({ ...prev, [merged.id]: merged }));
          if (onRecordUpdated) onRecordUpdated(merged);
        }
      } else {
        toast.error(resp.EM || 'Lỗi cập nhật');
      }
    } catch {
      toast.error('Lỗi kết nối server');
    } finally {
      setSaving(false);
    }
  };

  const handleCsdtStatusChange = useCallback(async (id: number, newStatus: CsdtStatus) => {
    try {
      const resp = await updateCsdtStatusApi(id, newStatus);
      if (resp.EC === 0) {
        toast.success('Cập nhật trạng thái CSĐT thành công');
        if (onRecordUpdated) {
          const rec = records.find(r => r.id === id);
          if (rec) onRecordUpdated({ ...rec, csdtStatus: newStatus });
        }
      } else {
        toast.error(resp.EM || 'Lỗi cập nhật');
      }
    } catch {
      toast.error('Lỗi kết nối server');
    }
  }, [records, onRecordUpdated]);

  const getDocsForRecord = (r: TtStudentRecord): TtStudentDocument[] => {
    return localDocs[r.id] ?? r.documents ?? [];
  };

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !docUploadRecordId) return;
    setUploadingDoc(true);
    try {
      const resp = await uploadDocumentApi(docUploadRecordId, file, docType);
      if (resp.EC === 0 && resp.DT) {
        setLocalDocs(prev => ({
          ...prev,
          [docUploadRecordId]: [...(prev[docUploadRecordId] ?? records.find(r => r.id === docUploadRecordId)?.documents ?? []), resp.DT],
        }));
        toast.success('Upload tài liệu thành công');
      } else {
        toast.error(resp.EM || 'Lỗi upload');
      }
    } catch {
      toast.error('Lỗi kết nối server');
    } finally {
      setUploadingDoc(false);
      setDocUploadRecordId(null);
      if (docFileRef.current) docFileRef.current.value = '';
    }
  };

  const handleDocDelete = async (docId: number, recordId: number) => {
    try {
      const resp = await deleteDocumentApi(docId);
      if (resp.EC === 0) {
        setLocalDocs(prev => ({
          ...prev,
          [recordId]: (prev[recordId] ?? records.find(r => r.id === recordId)?.documents ?? []).filter(d => d.id !== docId),
        }));
        toast.success('Xóa tài liệu thành công');
      }
    } catch {
      toast.error('Lỗi xóa tài liệu');
    }
  };

  if (loading) {
    return (
      <div className="tt-records__empty">
        <span className="tt-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
        <p>Đang tải hồ sơ...</p>
      </div>
    );
  }

  return (
    <>
      <div className="tt-records__toolbar">
        <div className="tt-records__search">
          <i className="material-icons">search</i>
          <input
            type="text"
            placeholder="Tìm theo tên, CCCD..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="tt-records__count">
          {search ? `${filtered.length} kết quả` : `${records.length}${total !== undefined && total > records.length ? ` / ${total}` : ''} hồ sơ`}
        </span>
        {failedCount > 0 && (
          <span className="tt-records__convert-badge tt-records__convert-badge--standalone" title={`${failedCount} hồ sơ chưa chuyển đổi được — bấm icon ở từng dòng để thử lại`}>
            <i className="material-icons">error_outline</i>
            {failedCount} lỗi ĐVHC
          </span>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && canWrite && courseStatus === 'open' && (
        <div className="tt-records__bulk-bar">
          <span>
            <i className="material-icons">check_box</i>
            Đã chọn {selectedIds.size} hồ sơ chưa duyệt
          </span>
          <button
            type="button"
            className="tt-btn tt-btn--primary tt-btn--sm"
            onClick={onOpenTransferModal}
          >
            <i className="material-icons">drive_file_move</i>
            Chuyển khóa
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="tt-records__empty">
          <i className="material-icons">inbox</i>
          <p>{records.length === 0 ? 'Chưa có hồ sơ nào' : 'Không tìm thấy kết quả'}</p>
        </div>
      ) : (
        <div className="tt-records__wrap">
          <table className="tt-records__table">
            <thead>
              <tr>
                {canWrite && courseStatus === 'open' && <th className="tt-records__select-col"></th>}
                <th>#</th>
                <th></th>
                <th className="tt-records__sortable" onClick={() => toggleSort('hoTen')}>
                  Họ tên
                  {sortCol === 'hoTen' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
                <th className="tt-records__sortable" onClick={() => toggleSort('soCCCD')}>
                  CCCD
                  {sortCol === 'soCCCD' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
                <th className="tt-records__sortable" onClick={() => toggleSort('dvhc')}>
                  Đơn vị hành chính
                  {sortCol === 'dvhc' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
                <th className="tt-records__sortable" onClick={() => toggleSort('teacher')}>
                  Giáo viên
                  {sortCol === 'teacher' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
                <th className="tt-records__sortable tt-records__gplx-col" onClick={() => toggleSort('hasGplx')}>
                  GPLX
                  {sortCol === 'hasGplx' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
                <th className="tt-records__sortable" onClick={() => toggleSort('scanMethod')}>
                  Cách nhập
                  {sortCol === 'scanMethod' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
                <th className="tt-records__sortable" onClick={() => toggleSort('reviewed')}>
                  Duyệt
                  {sortCol === 'reviewed' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
                <th className="tt-records__sortable" onClick={() => toggleSort('csdtStatus')}>
                  CSĐT
                  {sortCol === 'csdtStatus' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
                <th className="tt-records__sortable" onClick={() => toggleSort('createdAt')}>
                  Thời gian nhập
                  {sortCol === 'createdAt' && <i className="material-icons tt-records__sort-icon">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, idx) => {
                const method = METHOD_MAP[r.scanMethod] || METHOD_MAP.manual;
                const isExpanded = expandedId === r.id;
                const isEditing = editingId === r.id;

                return (
                  <tr
                    key={r.id}
                    onClick={() => void openDetail(r.id)}
                    style={{ cursor: 'pointer' }}
                    className={selectedIds.has(r.id) ? 'tt-records__row--selected' : undefined}
                  >
                    {canWrite && courseStatus === 'open' && (
                      <td className="tt-records__select-col" onClick={e => e.stopPropagation()}>
                        {!r.reviewed && (
                          <input
                            type="checkbox"
                            className="tt-records__select-checkbox"
                            checked={selectedIds.has(r.id)}
                            onChange={() => onToggleSelect(r.id)}
                          />
                        )}
                      </td>
                    )}
                    <td>{idx + 1}</td>
                    <td>
                      {/* Display avatar prefers the operator-uploaded portrait
                          (ảnh thẻ); falls back to the CCCD-cropped face or
                          stored avatarUrl. The CCCD avatar is the source of
                          truth for face matching and stays separate in the
                          edit modal — list view shows the "real" avatar. */}
                      {/* List view: use URL path (served by backend static handler).
                          Base64 blobs are no longer included in the list API —
                          they are fetched on demand when the operator expands a row. */}
                      {assetUrl(r.avatarUrl) ? (
                        <img
                          src={assetUrl(r.avatarUrl)!}
                          alt=""
                          className="tt-records__avatar"
                        />
                      ) : (
                        <div className="tt-records__avatar-placeholder">
                          <i className="material-icons">person</i>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="tt-records__name-cell" onMouseEnter={e => showTip(e, r.hoTen)} onMouseLeave={hideTip}>
                        <span className="tt-records__name">{r.hoTen}</span>
                        <span className="tt-records__name-sub">
                          Hạng • {fmtDate(r.ngaySinh) || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="tt-records__cccd" onMouseEnter={e => showTip(e, r.soCCCD)} onMouseLeave={hideTip}>{r.soCCCD}</td>
                    <td
                      className={`tt-records__address-cell tt-records__address-cell--${r.dvhcStatus || 'pending'}`}
                      onMouseEnter={e => showTip(e, r.dvhcError || r.dvhcTenDayDu || r.dvhcInfo?.tenDayDu || r.maDvhc || 'Chưa chuyển đổi')}
                      onMouseLeave={hideTip}
                    >
                      <div className="tt-records__address">
                        {r.dvhcStatus === 'failed' && (
                          <i className="material-icons tt-records__address-icon">error</i>
                        )}
                        {r.dvhcStatus === 'pending' && !r.dvhcTenDayDu && (
                          <i className="material-icons tt-records__address-icon">schedule</i>
                        )}
                        <span>{r.dvhcTenDayDu || r.dvhcInfo?.tenDayDu || r.maDvhc || (r.dvhcStatus === 'failed' ? 'Lỗi chuyển đổi' : 'Chưa chuyển đổi')}</span>
                        {canWrite && (r.dvhcStatus === 'pending' || r.dvhcStatus === 'failed') && (
                          <button
                            type="button"
                            className="tt-records__row-convert-btn"
                            onClick={(e) => handleConvertOneRecord(r.id, e)}
                            disabled={convertingRowId !== null}
                            title="Chuyển đổi địa chỉ này"
                          >
                            <i className="material-icons">
                              {convertingRowId === r.id ? 'hourglass_top' : 'autorenew'}
                            </i>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="tt-records__teacher-col">
                      {r.teacher?.username || <span className="tt-records__teacher-empty">—</span>}
                    </td>
                    <td className="tt-records__gplx-col">
                      {r.soGPLX ? (
                        <span className="tt-records__gplx-badge" title={`GPLX: ${r.soGPLX}`}>
                          {r.hangGPLXMoi || r.hangGPLX || 'GPLX'}
                        </span>
                      ) : (
                        <span className="tt-records__teacher-empty">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`tt-records__method ${method.cls}`}>
                        {method.label}
                      </span>
                    </td>
                    <td className="tt-records__reviewed-col">
                      {r.reviewed ? (
                        <span className="tt-records__reviewed-chip tt-records__reviewed-chip--yes" title="Đã duyệt">
                          Đã duyệt
                        </span>
                      ) : (
                        <span className="tt-records__reviewed-chip tt-records__reviewed-chip--no" title="Chưa duyệt">
                          Chưa duyệt
                        </span>
                      )}
                    </td>
                    <td className="tt-records__csdt-col">
                      {r.csdtStatus === 'none' && <span className="tt-records__csdt-badge tt-records__csdt-badge--none">—</span>}
                      {r.csdtStatus === 'entered' && <span className="tt-records__csdt-badge tt-records__csdt-badge--entered">Đã nhập</span>}
                      {r.csdtStatus === 'verified' && <span className="tt-records__csdt-badge tt-records__csdt-badge--verified">Đối sánh</span>}
                      {r.csdtStatus === 'completed' && <span className="tt-records__csdt-badge tt-records__csdt-badge--completed">Xong</span>}
                      {r.csdtStatus === 'failed' && <span className="tt-records__csdt-badge tt-records__csdt-badge--failed">Lỗi</span>}
                    </td>
                    <td className="tt-records__entered-at-col">
                      {r.createdAt ? (
                        <span className="tt-records__entered-at" title={new Date(r.createdAt).toLocaleString('vi-VN')}>
                          {new Date(r.createdAt).toLocaleDateString('vi-VN')}
                          <span className="tt-records__entered-at-time">
                            {new Date(r.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination bar — only shown when parent passes total + onPageChange */}
      {onPageChange && total !== undefined && total > pageSize && (
        <div className="tt-records__pagination">
          <button
            type="button"
            className="tt-btn tt-btn--outline tt-btn--sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <i className="material-icons">chevron_left</i>
          </button>
          {Array.from({ length: Math.ceil(total / pageSize) }, (_, i) => i + 1)
            .filter(p => p === 1 || p === Math.ceil(total / pageSize) || Math.abs(p - page) <= 2)
            .reduce<(number | '…')[]>((acc, p, i, arr) => {
              if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('…');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === '…'
                ? <span key={`ellipsis-${i}`} className="tt-records__pagination-ellipsis">…</span>
                : (
                  <button
                    key={p}
                    type="button"
                    className={`tt-btn tt-btn--sm ${page === p ? 'tt-btn--primary' : 'tt-btn--outline'}`}
                    onClick={() => onPageChange(p as number)}
                  >
                    {p}
                  </button>
                )
            )}
          <button
            type="button"
            className="tt-btn tt-btn--outline tt-btn--sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= Math.ceil(total / pageSize)}
          >
            <i className="material-icons">chevron_right</i>
          </button>
          <span className="tt-records__pagination-info">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} / {total} hồ sơ
          </span>
        </div>
      )}

      {/* Record detail modal — portaled to body so the dashboard sticky
          header can never clip it (stacking-context safe). */}
      {expandedId && (() => {
        // Base record from the list (metadata, no images). Always present.
        const listRecord = records.find(rec => rec.id === expandedId);
        if (!listRecord) return null;
        // Fully-loaded record from cache (includes base64 images + documents).
        // Falls back to listRecord while the detail fetch is in flight.
        const r: TtStudentRecord = detailCache[expandedId] ?? listRecord;
        const isEditing = editingId === r.id;
        const method = METHOD_MAP[r.scanMethod] || METHOD_MAP.manual;
        // Walk the filtered list (what the operator actually sees in the
        // table) so Prev/Next respects the current search + filters.
        const navIdx = filtered.findIndex(rec => rec.id === expandedId);
        const hasPrev = navIdx > 0;
        const hasNext = navIdx >= 0 && navIdx < filtered.length - 1;
        const goPrev = () => {
          if (!hasPrev || isEditing) return;
          void openDetail(filtered[navIdx - 1].id);
          setConfirmDeleteId(null);
        };
        const goNext = () => {
          if (!hasNext || isEditing) return;
          void openDetail(filtered[navIdx + 1].id);
          setConfirmDeleteId(null);
        };

        return createPortal(
          <div className="tt-detail-modal__overlay" onClick={() => { if (!isEditing) { setExpandedId(null); } }}>
            <div className="tt-detail-modal" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="tt-detail-modal__header">
                <div className="tt-detail-modal__header-info">
                  <h3>
                    <i className="material-icons">person</i>
                    {r.hoTen}
                    {detailLoading && !detailCache[expandedId] && (
                      <span className="tt-spinner" style={{ width: 14, height: 14, borderWidth: 2, marginLeft: 8 }} />
                    )}
                  </h3>
                  <div className="tt-detail-modal__header-badges">
                    <span className="tt-detail-modal__badge">{r.soCCCD}</span>
                    <span className={`tt-records__method ${method.cls}`}>
                      <i className="material-icons">{method.icon}</i>
                      {method.label}
                    </span>
                    {/* Interactive review toggle — moved from the
                        outside row into the popup so approval always
                        happens after the operator has looked at the
                        full record. Read-only chip in row = status
                        only; this toggle = action. */}
                    {canWrite && courseStatus === 'open' ? (
                      <button
                        type="button"
                        className={`tt-detail-modal__review-toggle ${r.reviewed ? 'tt-detail-modal__review-toggle--yes' : 'tt-detail-modal__review-toggle--no'}`}
                        onClick={() => onToggleReviewed(r.id, !r.reviewed)}
                        disabled={isEditing}
                        title={r.reviewed ? 'Bỏ duyệt hồ sơ' : 'Duyệt hồ sơ'}
                      >
                        <i className="material-icons">
                          {r.reviewed ? 'check_circle' : 'radio_button_unchecked'}
                        </i>
                        {r.reviewed ? 'Đã duyệt' : 'Duyệt hồ sơ'}
                      </button>
                    ) : (
                      r.reviewed && (
                        <span className="tt-detail-modal__badge tt-detail-modal__badge--success">
                          <i className="material-icons">check_circle</i> Đã duyệt
                        </span>
                      )
                    )}
                    {canWrite ? (
                      <select
                        className={`tt-records__csdt-select ${CSDT_OPTIONS.find(o => o.value === r.csdtStatus)?.cls || ''}`}
                        value={r.csdtStatus}
                        onChange={e => handleCsdtStatusChange(r.id, e.target.value as CsdtStatus)}
                      >
                        {CSDT_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      (() => {
                        const opt = CSDT_OPTIONS.find(o => o.value === r.csdtStatus);
                        return opt && opt.value !== 'none' ? (
                          <span className={`tt-records__csdt-badge ${opt.cls}`}>
                            <i className="material-icons">{opt.icon}</i> {opt.label}
                          </span>
                        ) : null;
                      })()
                    )}
                  </div>
                </div>
                <div className="tt-detail-modal__header-actions">
                  {canWrite && courseStatus === 'open' && !isEditing && (
                    <>
                      <button type="button" className="tt-btn tt-btn--primary tt-btn--sm" onClick={e => startEdit(r, e)}>
                        <i className="material-icons">edit</i> Sửa
                      </button>
                      {/* Chuyển khóa (single record) — operator picks
                          target from eligible list (same hang, status=
                          open). Only enabled for unreviewed records. */}
                      {!r.reviewed && onOpenSingleTransfer && (
                        <button
                          type="button"
                          className="tt-btn tt-btn--outline tt-btn--sm"
                          onClick={e => { e.stopPropagation(); onOpenSingleTransfer(r.id); }}
                          title="Chuyển hồ sơ sang khóa khác cùng hạng"
                          style={{ color: '#1976d2', borderColor: '#90caf9' }}
                        >
                          <i className="material-icons">drive_file_move</i> Chuyển khóa
                        </button>
                      )}
                      {confirmDeleteId === r.id ? (
                        <>
                          <button type="button" className="tt-btn tt-btn--danger tt-btn--sm" onClick={e => handleDelete(r.id, e)}>
                            <i className="material-icons">check</i> Xác nhận
                          </button>
                          <button type="button" className="tt-btn tt-btn--ghost tt-btn--sm" onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}>
                            <i className="material-icons">close</i> Hủy
                          </button>
                        </>
                      ) : (
                        <button type="button" className="tt-btn tt-btn--danger tt-btn--sm" onClick={e => { e.stopPropagation(); setConfirmDeleteId(r.id); }}>
                          <i className="material-icons">delete</i> Xóa
                        </button>
                      )}
                    </>
                  )}
                  {/* Prev / Next navigation through the filtered table
                      order. Disabled in edit mode so the operator can't
                      accidentally abandon unsaved changes. */}
                  <div className="tt-detail-modal__nav" role="group" aria-label="Điều hướng hồ sơ">
                    <button
                      type="button"
                      className="tt-detail-modal__nav-btn"
                      onClick={goPrev}
                      disabled={!hasPrev || isEditing}
                      title={isEditing ? 'Hoàn tất chỉnh sửa trước' : 'Hồ sơ trước (←)'}
                      aria-label="Hồ sơ trước"
                    >
                      <i className="material-icons">chevron_left</i>
                    </button>
                    <span className="tt-detail-modal__nav-pos">
                      {navIdx >= 0 ? `${navIdx + 1} / ${filtered.length}` : ''}
                    </span>
                    <button
                      type="button"
                      className="tt-detail-modal__nav-btn"
                      onClick={goNext}
                      disabled={!hasNext || isEditing}
                      title={isEditing ? 'Hoàn tất chỉnh sửa trước' : 'Hồ sơ tiếp theo (→)'}
                      aria-label="Hồ sơ tiếp theo"
                    >
                      <i className="material-icons">chevron_right</i>
                    </button>
                  </div>
                  <button type="button" className="tt-detail-modal__close" onClick={() => { setExpandedId(null); setEditingId(null); setConfirmDeleteId(null); }}>
                    <i className="material-icons">close</i>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="tt-detail-modal__body">
                <div className="tt-detail-modal__layout">
                  {/* Avatar column */}
                  <div className="tt-records__detail-avatar-col">
                    {/* CCCD avatar — read-only reference image. Cropped
                        from the scanned CCCD card. No "Đổi ảnh" button:
                        the face on the CCCD is the source of truth for
                        the face-match comparison and must never change
                        from the edit modal. */}
                    <div
                      className="tt-records__detail-img-wrap tt-records__detail-img-wrap--cccd"
                      onClick={() => {
                        const url = imgSrc(r.avatarBase64, r.avatarUrl);
                        if (url) setZoomAvatarUrl(url);
                      }}
                      style={{ cursor: imgSrc(r.avatarBase64, r.avatarUrl) ? 'zoom-in' : 'default' }}
                      title="Ảnh trên CCCD — chỉ để đối chiếu"
                    >
                      {imgSrc(r.avatarBase64, r.avatarUrl) ? (
                        <img src={imgSrc(r.avatarBase64, r.avatarUrl)!} alt="Ảnh CCCD" className="tt-records__detail-img" />
                      ) : (
                        <div className="tt-records__detail-img-placeholder">
                          <i className="material-icons">badge</i>
                          <span>Chưa có ảnh CCCD</span>
                        </div>
                      )}
                      <div className="tt-records__detail-img-label">
                        <i className="material-icons">badge</i>
                        Ảnh CCCD (đối chiếu)
                      </div>
                    </div>
                    {/* Portrait (ảnh đại diện) — uploaded by the operator.
                        After save, this becomes the student's displayed
                        avatar everywhere (list, card, print). Every upload
                        re-runs the face match with the CCCD avatar above. */}
                    <PortraitUploader
                      cccdAvatarBase64={r.avatarBase64 || r.avatarUrl || null}
                      portraitBase64={isEditing ? editPortraitBase64 : (r.portraitBase64 ?? null)}
                      portraitOriginalBase64={isEditing ? editPortraitOriginalBase64 : (r.portraitOriginalBase64 ?? null)}
                      portraitAiVariantsJson={isEditing ? editPortraitAiVariantsJson : (r.portraitAiVariantsJson ?? null)}
                      faceMatchScore={isEditing ? editFaceMatchScore : (r.faceMatchScore ?? null)}
                      onChange={(pb, score, pbOrig, variants) => {
                        setEditPortraitBase64(pb);
                        setEditPortraitOriginalBase64(pbOrig ?? null);
                        setEditPortraitAiVariantsJson(variants ?? null);
                        setEditFaceMatchScore(score);
                      }}
                      disabled={!isEditing || saving}
                    />
                    {/* Ảnh gốc trước khi AI xử lý — chỉ hiển thị ở
                        chế độ xem (không edit) để operator đối chiếu
                        ảnh AI vs ảnh thật. Click để phóng to. */}
                    {!isEditing && r.portraitOriginalBase64 && (
                      <div className="tt-records__detail-portrait-original">
                        <div className="tt-records__detail-portrait-original-label">
                          <i className="material-icons">history</i>
                          Ảnh gốc (trước AI)
                        </div>
                        <img
                          src={r.portraitOriginalBase64.startsWith('data:')
                            ? r.portraitOriginalBase64
                            : `data:image/jpeg;base64,${r.portraitOriginalBase64}`}
                          alt="Ảnh gốc"
                          className="tt-records__detail-portrait-original-img"
                          onClick={() => setZoomAvatarUrl(
                            r.portraitOriginalBase64!.startsWith('data:')
                              ? r.portraitOriginalBase64!
                              : `data:image/jpeg;base64,${r.portraitOriginalBase64}`
                          )}
                          title="Phóng to"
                        />
                      </div>
                    )}
                    {/* Meta */}
                    <div className="tt-detail-modal__meta">
                      <span><i className="material-icons">schedule</i> {new Date(r.createdAt).toLocaleString('vi-VN')}</span>
                      <span><i className="material-icons">person</i> {r.enteredByUser?.username || '—'}</span>
                    </div>
                  </div>

                  {/* Fields column */}
                  <div className="tt-detail-modal__fields-col">
                    <div className="tt-records__detail-fields">
                      {DETAIL_FIELDS.map(({ key, label, icon }) => (
                        <div
                          className={`tt-records__detail-field${WIDE_FIELDS.has(key) ? ' tt-records__detail-field--wide' : ''}`}
                          key={key}
                        >
                          <i className="material-icons tt-records__detail-field-icon">{icon}</i>
                          <div className="tt-records__detail-field-content">
                            <span className="tt-records__detail-field-label">{label}</span>
                            {key === 'maDvhc' ? (
                              isEditing ? (
                                <div className="tt-records__detail-dvhc-wrap">
                                  <DvhcAutocomplete
                                    value={editDraft.maDvhc || ''}
                                    displayText={dvhcDisplayText}
                                    onChange={(maDvhc, tenDayDu) => { setDvhcDisplayText(tenDayDu); handleEditField('maDvhc', maDvhc); }}
                                  />
                                  {autoConvertBusy && (
                                    <span className="tt-records__detail-dvhc-spinner" title="Đang chuyển đổi từ địa chỉ gốc...">
                                      <i className="material-icons">autorenew</i>
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span
                                  className={`tt-records__detail-field-value tt-records__detail-field-value--${r.dvhcStatus || 'pending'}`}
                                  onMouseEnter={e => showTip(e, r.dvhcError || r.dvhcTenDayDu || r.dvhcInfo?.tenDayDu || r.maDvhc || 'Chưa chuyển đổi')}
                                  onMouseLeave={hideTip}
                                >
                                  {r.dvhcTenDayDu || r.dvhcInfo?.tenDayDu || r.maDvhc || (r.dvhcStatus === 'failed' ? `Lỗi: ${r.dvhcError || 'chuyển đổi thất bại'}` : 'Chưa chuyển đổi')}
                                </span>
                              )
                            ) : isEditing ? (
                              <input
                                type="text"
                                className="tt-records__detail-field-input"
                                value={editDraft[key] || ''}
                                onChange={e => handleEditField(key, e.target.value)}
                                placeholder={label}
                              />
                            ) : (
                              <span
                                className="tt-records__detail-field-value"
                                {...(MAGNIFY_KEYS.has(key) ? {
                                  onMouseEnter: (e: React.MouseEvent) => showTip(e, ((r as Record<string, unknown>)[key] as string) || '—'),
                                  onMouseLeave: hideTip,
                                } : {})}
                              >
                                {(key === 'ngaySinh' || key === 'ngayCap')
                                  ? fmtDate((r as Record<string, unknown>)[key] as string | null) || '—'
                                  : (r as Record<string, unknown>)[key] as string || '—'}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Teacher */}
                    <div className="tt-records__detail-field tt-records__detail-field--wide tt-records__detail-field--teacher">
                      <i className="material-icons tt-records__detail-field-icon">school</i>
                      <div className="tt-records__detail-field-content">
                        <span className="tt-records__detail-field-label">Giáo viên phụ trách</span>
                        {isEditing ? (
                          <TeacherAutocomplete
                            value={editTeacherId ?? null}
                            initialDisplayName={editTeacherName ?? undefined}
                            onChange={(id, name) => { setEditTeacherId(id); setEditTeacherName(name); }}
                            disabled={saving}
                          />
                        ) : (
                          <span className="tt-records__detail-field-value">
                            {r.teacher?.username || '—'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* GPLX */}
                    {(r.soGPLX || r.hangGPLX || isEditing) && (
                      <div className="tt-records__gplx-section">
                        <div className="tt-records__gplx-header">
                          <i className="material-icons">directions_car</i>
                          <span>Giấy phép lái xe</span>
                        </div>
                        <div className="tt-records__detail-fields">
                          {GPLX_DETAIL_FIELDS.map(({ key, label, icon, dropdown }) => (
                            <div className="tt-records__detail-field" key={key}>
                              <i className="material-icons tt-records__detail-field-icon">{icon}</i>
                              <div className="tt-records__detail-field-content">
                                <span className="tt-records__detail-field-label">{label}</span>
                                {isEditing && dropdown ? (
                                  <select
                                    className="tt-records__detail-field-input"
                                    value={editDraft[key] || ''}
                                    onChange={e => handleEditField(key, e.target.value)}
                                  >
                                    <option value="">— Chọn hạng —</option>
                                    {HANG_GPLX_MOI_OPTIONS.map(h => (
                                      <option key={h} value={h}>{h}</option>
                                    ))}
                                  </select>
                                ) : isEditing ? (
                                  <input
                                    type="text"
                                    className="tt-records__detail-field-input"
                                    value={editDraft[key] || ''}
                                    onChange={e => handleEditField(key, e.target.value)}
                                    placeholder={label}
                                  />
                                ) : (
                                  <span className="tt-records__detail-field-value">
                                    {(r as Record<string, unknown>)[key] as string || '—'}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                          {/* Số năm lái xe — derived from ngayTrungTuyen.
                              Read-only (auto badge); red warning when
                              < 3 years (common threshold for "new driver"). */}
                          {(() => {
                            const src = isEditing
                              ? (editDraft.ngayTrungTuyenGPLX || '')
                              : (r.ngayTrungTuyenGPLX || '');
                            const calc = calcDrivingYearsSafe(src);
                            return (
                              <div
                                className="tt-records__detail-field"
                                style={calc?.warning ? { background: '#fde7e7', borderColor: '#c62828' } : undefined}
                                title="Tự tính từ ngày trúng tuyển GPLX — không chỉnh sửa được trực tiếp"
                              >
                                <i
                                  className="material-icons tt-records__detail-field-icon"
                                  style={calc?.warning ? { color: '#c62828' } : undefined}
                                >timeline</i>
                                <div className="tt-records__detail-field-content">
                                  <span className="tt-records__detail-field-label">
                                    Số năm lái xe
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 3,
                                        marginLeft: 6,
                                        padding: '1px 6px',
                                        borderRadius: 3,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        background: '#e0e0e0',
                                        color: '#424242',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      <i className="material-icons" style={{ fontSize: 9 }}>lock</i> auto
                                    </span>
                                  </span>
                                  <span
                                    className="tt-records__detail-field-value"
                                    style={calc?.warning ? { color: '#c62828', fontWeight: 700 } : undefined}
                                  >
                                    {calc ? `${Math.floor(calc.years)} năm` : '—'}
                                    {calc?.warning && (
                                      <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 600 }}>
                                        ⚠ Chưa đủ 3 năm
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Image management — edit mode */}
                    {isEditing && (
                      <div className="tt-records__edit-images">
                        <div className="tt-records__edit-images-header">
                          <i className="material-icons">collections</i>
                          <span>Quản lý hình ảnh</span>
                        </div>
                        <div className="tt-records__edit-images-grid">
                          <div className="tt-records__edit-img-card">
                            <span className="tt-records__edit-img-label">
                              <i className="material-icons">photo_camera</i> Ảnh CCCD gốc
                            </span>
                            <div className="tt-records__edit-img-preview">
                              {(editSourcePreview || imgSrc(r.sourceImageBase64, r.sourceImageUrl)) ? (
                                <img
                                  src={editSourcePreview || imgSrc(r.sourceImageBase64, r.sourceImageUrl)!}
                                  alt="CCCD gốc"
                                  onClick={() => setViewImageUrl(editSourcePreview || imgSrc(r.sourceImageBase64, r.sourceImageUrl))}
                                />
                              ) : (
                                <div className="tt-records__edit-img-empty">
                                  <i className="material-icons">image</i>
                                  <span>Chưa có</span>
                                </div>
                              )}
                            </div>
                            <button type="button" className="tt-btn tt-btn--sm tt-btn--outline" onClick={() => sourceFileRef.current?.click()}>
                              <i className="material-icons">upload</i>
                              {imgSrc(r.sourceImageBase64, r.sourceImageUrl) || editSourcePreview ? 'Thay ảnh' : 'Tải lên'}
                            </button>
                          </div>

                          {getDocsForRecord(r).map(doc => {
                            const dt = DOC_TYPES.find(d => d.value === doc.docType) || DOC_TYPES[3];
                            return (
                              <div key={doc.id} className="tt-records__edit-img-card">
                                <span className="tt-records__edit-img-label">
                                  <i className="material-icons">{dt.icon}</i> {doc.label || dt.label}
                                </span>
                                <div className="tt-records__edit-img-preview">
                                  <img src={doc.fileBase64 || assetUrl(doc.fileUrl) || ''} alt={doc.label || dt.label} onClick={() => setViewImageUrl(doc.fileBase64 || assetUrl(doc.fileUrl))} />
                                  <button type="button" className="tt-records__edit-img-delete" onClick={() => handleDocDelete(doc.id, r.id)} title="Xóa">
                                    <i className="material-icons">close</i>
                                  </button>
                                </div>
                              </div>
                            );
                          })}

                          <div className="tt-records__edit-img-card tt-records__edit-img-card--add">
                            <span className="tt-records__edit-img-label">
                              <i className="material-icons">add_photo_alternate</i> Thêm giấy tờ
                            </span>
                            <div className="tt-records__edit-img-add-body">
                              <select className="tt-records__docs-type-select" value={docType} onChange={e => setDocType(e.target.value as DocType)}>
                                {DOC_TYPES.map(dt => (<option key={dt.value} value={dt.value}>{dt.label}</option>))}
                              </select>
                              <button type="button" className="tt-btn tt-btn--sm tt-btn--primary" onClick={() => { setDocUploadRecordId(r.id); docFileRef.current?.click(); }} disabled={uploadingDoc}>
                                <i className="material-icons">upload</i>
                                {uploadingDoc && docUploadRecordId === r.id ? 'Đang tải...' : 'Chọn ảnh'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Giấy tờ đính kèm — single section showing all
                        attached docs from 3 sources:
                          1. CCCD source image (sourceImageBase64/Url)
                          2. extraImagesJson (GPLX/CCCD-back/health/misc
                             carried from pending review at save time)
                          3. tt_student_document rows uploaded via the
                             "Thêm giấy tờ" button below
                        Previously these were split across two sections
                        ("Ảnh bổ sung" + "Giấy tờ đính kèm"); operators
                        opened the popup, looked at the latter, and saw
                        only "Chưa có" because their docs were sitting
                        in the former. Merging removes that confusion. */}
                    {!isEditing && (() => {
                      let extras: Array<{ base64: string; docType?: string; label?: string }> = [];
                      try {
                        if (r.extraImagesJson) {
                          const parsed = JSON.parse(r.extraImagesJson);
                          if (Array.isArray(parsed)) extras = parsed;
                        }
                      } catch { /* ignore malformed JSON */ }

                      const cccdSrc = imgSrc(r.sourceImageBase64, r.sourceImageUrl);
                      const docs = getDocsForRecord(r);
                      const totalCount = (cccdSrc ? 1 : 0) + extras.length + docs.length;

                      return (
                        <div className="tt-records__docs">
                          <div className="tt-records__docs-header">
                            <span className="tt-records__docs-title">
                              <i className="material-icons">folder</i>
                              Giấy tờ đính kèm{totalCount > 0 ? ` (${totalCount})` : ''}
                            </span>
                            {canWrite && courseStatus === 'open' && (
                              <div className="tt-records__docs-upload">
                                <select className="tt-records__docs-type-select" value={docType} onChange={e => setDocType(e.target.value as DocType)}>
                                  {DOC_TYPES.map(dt => (<option key={dt.value} value={dt.value}>{dt.label}</option>))}
                                </select>
                                <button type="button" className="tt-btn tt-btn--sm tt-btn--outline" onClick={() => { setDocUploadRecordId(r.id); docFileRef.current?.click(); }} disabled={uploadingDoc}>
                                  <i className="material-icons">upload</i>
                                  {uploadingDoc && docUploadRecordId === r.id ? 'Đang tải...' : 'Thêm giấy tờ'}
                                </button>
                              </div>
                            )}
                          </div>
                          {totalCount > 0 ? (
                            <div className="tt-records__docs-grid">
                              {/* CCCD source image — read-only reference */}
                              {cccdSrc && (
                                <div className="tt-records__doc-card">
                                  <img src={cccdSrc} alt="Ảnh CCCD gốc" className="tt-records__doc-thumb" onClick={() => setViewImageUrl(cccdSrc)} />
                                  <div className="tt-records__doc-info">
                                    <span className="tt-records__doc-type"><i className="material-icons">photo_camera</i>Ảnh CCCD gốc</span>
                                  </div>
                                </div>
                              )}
                              {/* Extras from pending review — read-only;
                                  delete happens upstream in pending review,
                                  not here, so no × button. */}
                              {extras.map((img, i) => {
                                const dt = DOC_TYPES.find(d => d.value === img.docType) || DOC_TYPES[3];
                                const src = img.base64.startsWith('data:')
                                  ? img.base64
                                  : `data:image/jpeg;base64,${img.base64}`;
                                return (
                                  <div key={`extra-${i}`} className="tt-records__doc-card">
                                    <img src={src} alt={img.label || dt.label} className="tt-records__doc-thumb" onClick={() => setViewImageUrl(src)} />
                                    <div className="tt-records__doc-info">
                                      <span className="tt-records__doc-type"><i className="material-icons">{dt.icon}</i>{dt.label}</span>
                                    </div>
                                  </div>
                                );
                              })}
                              {/* Uploaded documents — deletable when open */}
                              {docs.map(doc => {
                                const dt = DOC_TYPES.find(d => d.value === doc.docType) || DOC_TYPES[3];
                                return (
                                  <div key={`doc-${doc.id}`} className="tt-records__doc-card">
                                    <img src={doc.fileBase64 || assetUrl(doc.fileUrl) || ''} alt={doc.label || dt.label} className="tt-records__doc-thumb" onClick={() => setViewImageUrl(doc.fileBase64 || assetUrl(doc.fileUrl))} />
                                    <div className="tt-records__doc-info">
                                      <span className="tt-records__doc-type"><i className="material-icons">{dt.icon}</i>{doc.label || dt.label}</span>
                                      {canWrite && courseStatus === 'open' && (
                                        <button type="button" className="tt-records__doc-delete" onClick={() => handleDocDelete(doc.id, r.id)} title="Xóa">
                                          <i className="material-icons">close</i>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="tt-records__docs-empty">Chưa có giấy tờ đính kèm</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Footer — edit actions */}
              {isEditing && (
                <div className="tt-detail-modal__footer">
                  <button type="button" className="tt-btn tt-btn--outline" onClick={cancelEdit} disabled={saving}>
                    <i className="material-icons">close</i> Hủy
                  </button>
                  <button type="button" className="tt-btn tt-btn--success" onClick={saveEdit} disabled={saving}>
                    {saving ? <span className="tt-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : <i className="material-icons">save</i>}
                    Lưu thay đổi
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        );
      })()}

      {/* Hidden file inputs */}
      <input
        ref={docFileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleDocUpload}
      />
      <input
        ref={sourceFileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleEditSourceUpload}
      />

      {/* Image viewer modal */}
      {viewImageUrl && (
        <div className="tt-img-modal" onClick={() => setViewImageUrl(null)}>
          <div className="tt-img-modal__content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="tt-img-modal__close"
              onClick={() => setViewImageUrl(null)}
            >
              <i className="material-icons">close</i>
            </button>
            <img src={viewImageUrl} alt="CCCD gốc" className="tt-img-modal__img" />
          </div>
        </div>
      )}

      {/* Avatar zoom modal */}
      {zoomAvatarUrl && (
        <div className="tt-img-modal" onClick={() => setZoomAvatarUrl(null)}>
          <div className="tt-img-modal__content tt-img-modal__content--avatar" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="tt-img-modal__close"
              onClick={() => setZoomAvatarUrl(null)}
            >
              <i className="material-icons">close</i>
            </button>
            <img src={zoomAvatarUrl} alt="Avatar" className="tt-img-modal__img" />
          </div>
        </div>
      )}

      {tipText && createPortal(
        <div
          className={`tt-magnify-tip ${tipVisible ? 'tt-magnify-tip--visible' : ''}`}
          style={{ left: tipPos.x, top: tipPos.y, transform: `translateY(-100%)` }}
        >
          {tipText}
        </div>,
        document.body,
      )}
    </>
  );
};

export default TtRecordsTable